import React, { Fragment, FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../../styles/system.admin.scss";
import { Formik, Form, Field, FormikHelpers, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { isAuth } from "../../auth/ProtectedRoute";
import swal from "sweetalert";
import { FileResponse, GroupValue } from "./types/DataTypes";
import { ActivityLogValues } from "../ActivityLog/types/DataType";
import { CircularProgress } from "@mui/material";

const AddGroup: FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [coverLoading, setCoverLoading] = useState(false);
  const [groupImage, setGroupImage] = useState<FileResponse>(
    {} as FileResponse
  );
  const [groupCoverImage, setGroupCoverImage] = useState<FileResponse>(
    {} as FileResponse
  );
  const [categories, setCategories] = useState([]);

  //formik initialValues to setup add group form
  const initialValues: GroupValue = {
    name: "",
    privacy: "",
    admin: "",
    category: "",
    image: undefined,
    coverImage: undefined,
    type: "",
    description: "",
  };
  const FILE_SIZE = 2000000;
  const SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/png"];

  //request to get category data
  useEffect(() => {
    axios({
      method: "GET",
      url: process.env.REACT_APP_HOST_API + `category`,
      headers: {
        Authorization: `Bearer ${isAuth()}`,
      },
    })
      .then((res) => {
        setCategories(res.data.data.data);
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error!",
          text: "Something went wrong. Please try again later!",
          icon: "error",
        });
      });
  }, []);

  return (
    <Fragment>
      <div className="admin-edit">
        <div className="admin-edit-wrapper">
          <div className="admin-edit--heading">
            <div className="admin-edit--heading--title">
              <h1>Add Group</h1>
            </div>
            <div className="admin-edit--heading-btn">
              <button onClick={() => navigate(-1)}>
                <span>
                  <svg
                    width="22"
                    height="20"
                    viewBox="0 0 22 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 2L2 7L7 12"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2 7H10C15.523 7 20 11.477 20 17V18"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                Back
              </button>
            </div>
          </div>
        </div>
        <Formik
          initialValues={initialValues}
          validationSchema={Yup.object().shape({
            name: Yup.string().required("This field is required"),
            privacy: Yup.string().required("This field is required"),
            admin: Yup.string()
              .email("Invalid email address")
              .required("This field is required"),
            category: Yup.string().required("This field is required"),
            image: Yup.mixed()
              .required("You need to upload a file")
              .test(
                "fileSize",
                "File size is too large",
                (value) => value && value.size <= FILE_SIZE
              )
              .test(
                "fileFormat",
                "Unsupported Format",
                (value) => value && SUPPORTED_FORMATS.includes(value.type)
              ),
            coverImage: Yup.mixed()
              .required("You need to upload a file")
              .test(
                "fileSize",
                "File size is too large",
                (value) => value && value.size <= FILE_SIZE
              )
              .test(
                "fileFormat",
                "Unsupported Format",
                (value) => value && SUPPORTED_FORMATS.includes(value.type)
              ),
            type: Yup.string().required("This field is required"),
            description: Yup.string().required("This field is required"),
          })}
          onSubmit={(
            values: GroupValue,
            { setSubmitting }: FormikHelpers<GroupValue>
          ) => {
            //payload for group values
            const formData: any = new FormData();
            formData.append("name", values.name);
            formData.append("privacy", values.privacy);
            formData.append("admin", values.admin);
            formData.append("category", values.category);
            formData.append("image", groupImage?.filepath);
            formData.append("cover_image", groupCoverImage?.filepath);
            formData.append("type", values.type);
            formData.append("description", values.description);

            //request to submit group data to the server
            axios({
              method: "POST",
              url: process.env.REACT_APP_HOST_API + `manage-groups/group`,
              headers: {
                Authorization: `Bearer ${isAuth()}`,
              },
              data: formData,
            })
              .then((res) => {
                setSubmitting(false);
                if (res.data.response === true) {
                  //activity log request submitted to server
                  let activityLogItem;
                  if (typeof window !== "undefined") {
                    if (sessionStorage.getItem("activityDetail")) {
                      activityLogItem = JSON.parse(
                        sessionStorage.getItem("activityDetail")!
                      );
                    }
                  }
                  const activityDetail: ActivityLogValues = {
                    full_name: activityLogItem.full_name,
                    role: activityLogItem.role,
                    ip_address: activityLogItem.ip_address,
                    browser: activityLogItem.browser,
                    events: `Added new group: ${values.name}`,
                    module: "Content Management",
                    location: activityLogItem.location,
                    email: activityLogItem.email,
                  };
                  axios({
                    method: "POST",
                    url:
                      process.env.REACT_APP_HOST_API + "manage-activity/user",
                    headers: {
                      Authorization: `Bearer ${isAuth()}`,
                    },
                    data: activityDetail,
                  });
                  swal({
                    title: "Success!",
                    text: "Group added successfully!",
                    icon: "success",
                  });
                  navigate(-1);
                } else if (
                  // server error 401 handling
                  res.data.response === false &&
                  res.data.code === 401
                ) {
                  swal({
                    title: "Error!",
                    text: res.data.message,
                    icon: "error",
                  });
                } else if (
                  // server error 406 handling
                  res.data.response === false &&
                  res.data.code === 406
                ) {
                  swal({
                    title: "Error!",
                    text: res.data.message,
                    icon: "error",
                  });
                } else if (
                  // server error 409 handling
                  res.data.response === false &&
                  res.data.code === 409
                ) {
                  swal({
                    title: "Error!",
                    text: res.data.message,
                    icon: "error",
                  });
                } else if (
                  // server error 500 handling
                  res.data.response === false &&
                  res.data.code === 500
                ) {
                  swal({
                    title: "Error!",
                    text: "Something went wrong. Please try again later!",
                    icon: "error",
                  });
                }
              })
              .catch((err) => {
                console.error(err);
                swal({
                  title: "Error!",
                  text: "Something went wrong. Please try again later!",
                  icon: "error",
                });
              });
          }}
        >
          {({ setFieldValue, isSubmitting }) => (
            <>
              {/* <pre>{JSON.stringify({ values, errors }, null, 2)}</pre> */}
              <Form noValidate autoComplete="off">
                <div className="admin-edit--group">
                  <div className="admin-edit--group-form">
                    <div className="right">
                      <label htmlFor="name">Group Name</label>
                      <Field
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Enter group name"
                      />
                      <ErrorMessage name="name">
                        {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                      </ErrorMessage>
                    </div>
                    <div className="left">
                      <label htmlFor="role">Select Privacy</label>
                      <Field
                        as="select"
                        id="privacy"
                        name="privacy"
                        type="text"
                      >
                        <>
                          <option value="" disabled selected hidden>
                            --Please choose an option--
                          </option>
                          <option value="public">Public</option>
                          <option value="private">Private</option>
                        </>
                      </Field>
                      <ErrorMessage name="privacy">
                        {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                      </ErrorMessage>
                    </div>
                  </div>
                  <div className="admin-edit--group-form">
                    <div className="right">
                      <label htmlFor="admin">Select Admin(Email only)</label>
                      <Field
                        id="admin"
                        name="admin"
                        type="text"
                        placeholder="example@email.com"
                      />
                      <ErrorMessage name="admin">
                        {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                      </ErrorMessage>
                    </div>
                    <div className="left">
                      <label htmlFor="category">Select Category</label>
                      <Field
                        as="select"
                        id="category"
                        name="category"
                        type="text"
                      >
                        <option value="" disabled selected>
                          --Please choose an option--
                        </option>
                        {categories ? (
                          categories.map((cat: any) => (
                            //map categories from server to select element
                            <>
                              <option value={cat.name}>{cat.name}</option>
                            </>
                          ))
                        ) : (
                          <option value="" disabled>
                            Options not available
                          </option>
                        )}
                      </Field>
                      <ErrorMessage name="category">
                        {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                      </ErrorMessage>
                    </div>
                  </div>
                  <div className="admin-edit--group-form">
                    <div className="left">
                      <label htmlFor="image">
                        Image(JPEG, JPG, PNG)
                        <Field
                          id="image"
                          name="image"
                          type="file"
                          accept="image/png, image/jpg, image/jpeg"
                          value={""}
                          style={{ display: "none" }}
                          onChange={(e: any) => {
                            setFieldValue("image", e.currentTarget.files[0]);
                            //error handling & validation for media uploads request to server
                            const file = e.currentTarget.files[0];
                            const fileSize = Math.round(file.size / 1024);
                            if (fileSize > 5120) {
                              swal({
                                title: "Error!",
                                icon: "error",
                                text: "Selected image must be less than 5MB",
                              });
                              return;
                            } else {
                              //media upload request to server
                              const formdata = new FormData();
                              formdata.append(`files[0]`, file);
                              formdata.append(
                                "env",
                                `${process.env.REACT_APP_ENV}`
                              );
                              setLoading(true);
                              axios({
                                method: "POST",
                                url:
                                  process.env.REACT_APP_HOST_API +
                                  `uploads/file`,
                                data: formdata,
                              })
                                .then((res) => {
                                  setGroupImage(res?.data?.data[0]);
                                })
                                .then(() => {
                                  setLoading(false);
                                })
                                .catch((error) => {
                                  setLoading(false);
                                  console.log(error);
                                  swal({
                                    title: "Error!",
                                    text: "Something went wrong. Please try again later!",
                                    icon: "error",
                                  });
                                });
                            }
                          }}
                        />
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <input
                            type="text"
                            id="imageInput"
                            value={groupImage?.original_name}
                            style={{ width: "84%" }}
                          />
                          <span id="imageButton">Choose file</span>
                        </div>
                      </label>
                      <ErrorMessage name="image">
                        {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                      </ErrorMessage>
                      {loading && (
                        <CircularProgress
                          sx={{ color: "gray", mt: 2 }}
                          size={12}
                        />
                      )}
                    </div>
                    <div className="left">
                      <label htmlFor="coverImage">
                        Cover Image(JPEG, JPG, PNG)
                        <Field
                          id="coverImage"
                          name="coverImage"
                          type="file"
                          accept="image/png, image/jpg, image/jpeg"
                          value={""}
                          style={{ display: "none" }}
                          onChange={(e: any) => {
                            setFieldValue(
                              "coverImage",
                              e.currentTarget.files[0]
                            );
                            //error handling & validation for media uploads request to server
                            const file = e.currentTarget.files[0];
                            const fileSize = Math.round(file.size / 1024);
                            if (fileSize > 5120) {
                              swal({
                                title: "Error!",
                                icon: "error",
                                text: "Selected image must be less than 5MB",
                              });
                              return;
                            } else {
                              //media upload request to server
                              const formdata = new FormData();
                              formdata.append(`files[0]`, file);
                              formdata.append(
                                "env",
                                `${process.env.REACT_APP_ENV}`
                              );
                              setCoverLoading(true);
                              axios({
                                method: "POST",
                                url:
                                  process.env.REACT_APP_HOST_API +
                                  `uploads/file`,
                                data: formdata,
                              })
                                .then((res) => {
                                  setGroupCoverImage(res?.data?.data[0]);
                                })
                                .then(() => {
                                  setCoverLoading(false);
                                })
                                .catch((error) => {
                                  setCoverLoading(false);
                                  console.log(error);
                                  swal({
                                    title: "Error!",
                                    text: "Something went wrong. Please try again later!",
                                    icon: "error",
                                  });
                                });
                            }
                          }}
                        />
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <input
                            type="text"
                            id="imageInput"
                            value={groupCoverImage?.original_name}
                            style={{ width: "84%" }}
                          />
                          <span id="imageButton">Choose file</span>
                        </div>
                      </label>
                      <ErrorMessage name="coverImage">
                        {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                      </ErrorMessage>
                      {coverLoading && (
                        <CircularProgress
                          sx={{ color: "gray", mt: 2 }}
                          size={12}
                        />
                      )}
                    </div>
                  </div>
                  <div className="admin-edit--group-form">
                    <div className="right">
                      <label htmlFor="type">Select Type</label>
                      <Field as="select" id="type" name="type" type="text">
                        <>
                          <option value="" disabled selected hidden>
                            --Please choose an option--
                          </option>
                          <option value="social">Social</option>
                          <option value="professional">Professional</option>
                        </>
                      </Field>
                      <ErrorMessage name="type">
                        {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                      </ErrorMessage>
                    </div>
                    <div className="left"></div>
                  </div>
                  <div className="admin-edit--desc">
                    <label htmlFor="description">Description</label>
                    <Field
                      as="textarea"
                      name="description"
                      placeholder="Tell us about the group..."
                    />
                    <ErrorMessage name="description">
                      {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                    </ErrorMessage>
                  </div>
                </div>
                <div className="admin-edit--btn">
                  <button type="submit">
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </Form>
            </>
          )}
        </Formik>
      </div>
    </Fragment>
  );
};

export default AddGroup;
